/*
 * Copyright (c) 2026.
 * Spin in het Web B.V. Apeldoorn.
 * Deze software mag alleen gebruikt / bekeken / gewijzigd worden volgens de voorwaarden die zijn vastgelegd in een bijbehorende schriftelijke overeenkomst met Spin in het Web B.V.
 */

/**
 * Auteur: Jelmer Jellema - Spin in het Web B.V
 * Gegenereerd met Claude Code (AI), 25-07-2026 — golf a2 (05-09-2026): eigenaar in plaats van spiegel
 *
 * Root-service die de condtag-state bezit: de conditionele tags van het geladen model met per
 * tag of hij zichtbaar is, de actieve tag plus zijn cond/cons-state, en of het paneel open
 * staat. Sinds golf a2 is dit de bron. `DlModel` leest `condtags` en `condtag` hiervandaan voor
 * het opslagformaat en leidt er de conditieset-classes uit af; de modelacties van de samenwerk-baas
 * muteren ze via de schrijf-API hieronder.
 *
 * De plumbing rond een mutatie (samenwerking, undo, save, log) is van de samenwerk-baas: het paneel
 * geeft zijn intenties door via de actie-callbacks, die de baas bij zijn constructie registreert
 * (sinds golf a3 en a4; daarvoor deed ng1 dat).
 */
import {computed, Injectable, signal} from '@angular/core';

/**
 * De actieve conditieset: de tag plus of we in de conditie- ('cond') of consequentie-kant
 * ('cons') werken.
 */
export interface CondtagState {
    tag: string;
    state: 'cond' | 'cons';
}

/**
 * De door de samenwerk-baas geregistreerde actie-callbacks. Elke actie gaat de actietabel in
 * (samenwerking/undo/save/log); het paneel muteert de state niet zelf.
 */
export interface CondtagActies {
    /** Open of sluit het paneel (sinds 09-09-2026 lokaal, niet over de lijn). */
    toggleOpen: (open: boolean) => void;
    /** Maak deze tag de actieve condtag (start altijd in 'cond'). */
    kiesTag: (tag: string) => void;
    /** Wissel de state van de actieve condtag. */
    zetState: (state: 'cond' | 'cons') => void;
    /** Deselecteer de actieve condtag. */
    unsetTag: () => void;
    /** Toggle de zichtbaarheid van de elementen van een tag. */
    toggleView: (tag: string) => void;
    /** Maak een nieuwe condtag aan (en maak hem actief). */
    nieuweTag: (naam: string) => void;
    /** Hernoem een condtag. */
    wijzigTag: (oud: string, nieuw: string) => void;
    /** Verwijder een condtag inclusief zijn elementen. */
    verwijderTag: (tag: string) => void;
}

@Injectable({providedIn: 'root'})
export class CondtagService {

    /**
     * De actieve condtag, of null als er geen actief is. Documentstate: gaat als `condtag` mee in
     * het opslagformaat.
     */
    private readonly _condtag = signal<CondtagState | null>(null);

    /**
     * Alle in het geladen model beschikbare condtags, met per tag of hij zichtbaar is.
     * Documentstate: gaat als `condtags` mee in het opslagformaat.
     */
    private readonly _conditioneleTags = signal<Record<string, boolean>>({});

    /**
     * Of het paneel open staat. Sessie-UI: hoort niet in het opslagformaat.
     */
    private readonly _open = signal(false);

    /**
     * De door de samenwerk-baas geregistreerde actie-callbacks.
     */
    private acties: CondtagActies | null = null;

    /**
     * De actieve condtag als readonly signal.
     */
    readonly condtag = this._condtag.asReadonly();

    /**
     * De beschikbare condtags met hun zichtbaarheid als readonly signal.
     */
    readonly conditioneleTags = this._conditioneleTags.asReadonly();

    /**
     * Of het paneel open staat, als readonly signal.
     */
    readonly open = this._open.asReadonly();

    /**
     * De tagnamen, alfabetisch gesorteerd.
     */
    readonly tags = computed(() => Object.keys(this._conditioneleTags()).sort());

    /**
     * De tagnamen die op zichtbaar staan; de bron voor de conditieset-classes op de graaf.
     */
    readonly actieveTags = computed(
        () => Object.entries(this._conditioneleTags()).filter(([, zichtbaar]) => zichtbaar).map(([tag]) => tag));

    /**
     * Of het model condtags heeft.
     */
    readonly heeftCondtags = computed(() => this.tags().length > 0);

    /**
     * Initialisatie door de samenwerk-baas: de actie-callbacks.
     */
    initialiseer(acties: CondtagActies): void {
        this.acties = acties;
    }

    /* ***************** SCHRIJF-API (golf a2) ***************** */

    /**
     * Zet de stand terug naar die van een leeg model: geen tags, geen actieve tag, paneel dicht.
     * Wordt aangeroepen bij het opstarten van het bouwscherm; het laden van een model zet daarna de
     * tags uit de modeldata.
     */
    reset(): void {
        this._conditioneleTags.set({});
        this._condtag.set(null);
        this._open.set(false);
    }

    /**
     * Maak deze tag plus state de actieve condtag, of `null` voor geen enkele. We bewaren een
     * eigen object, zodat een aangeleverde stand (uit de modeldata of uit een samenwerkbericht)
     * niet met ons meemuteert.
     */
    zetActieveTag(stand: CondtagState | null): void {
        this._condtag.set(stand ? {tag: stand.tag, state: stand.state} : null);
    }

    /**
     * Vervang de volledige tagverzameling, bijvoorbeeld bij het laden van een model.
     */
    zetTags(tags: Record<string, boolean>): void {
        this._conditioneleTags.set({...tags});
    }

    /**
     * Zet de zichtbaarheid van één tag; een nog onbekende tag komt er zo bij.
     */
    zetZichtbaar(tag: string, zichtbaar: boolean): void {
        this._conditioneleTags.set({...this._conditioneleTags(), [tag]: zichtbaar});
    }

    /**
     * Hernoem een tag met behoud van zijn zichtbaarheid; is hij de actieve condtag, dan verhuist
     * die mee (met dezelfde state).
     */
    hernoemTag(oud: string, nieuw: string): void {
        const tags: Record<string, boolean> = {};
        for (const [tag, zichtbaar] of Object.entries(this._conditioneleTags())) {
            tags[tag === oud ? nieuw : tag] = zichtbaar;
        }
        this._conditioneleTags.set(tags);
        const actief = this._condtag();
        if (actief?.tag === oud) {
            this._condtag.set({tag: nieuw, state: actief.state});
        }
    }

    /**
     * Haal een tag uit de verzameling. De actieve condtag en het paneel blijven zoals ze zijn;
     * de aanroeper bepaalt wat daarmee moet gebeuren.
     */
    wisTag(tag: string): void {
        const tags = {...this._conditioneleTags()};
        delete tags[tag];
        this._conditioneleTags.set(tags);
    }

    /**
     * Zet het paneel open of dicht.
     */
    zetOpen(open: boolean): void {
        this._open.set(!!open);
    }

    /* ***************** ACTIE-CALLBACKS RICHTING DE SAMENWERK-BAAS ***************** */

    /** Open of sluit het paneel. */
    toggleOpen(open: boolean): void {
        this.acties?.toggleOpen(open);
    }

    /** Maak deze tag de actieve condtag. */
    kiesTag(tag: string): void {
        this.acties?.kiesTag(tag);
    }

    /** Wissel de state van de actieve condtag. */
    zetState(state: 'cond' | 'cons'): void {
        this.acties?.zetState(state);
    }

    /** Deselecteer de actieve condtag. */
    unsetTag(): void {
        this.acties?.unsetTag();
    }

    /** Toggle de zichtbaarheid van de elementen van een tag. */
    toggleView(tag: string): void {
        this.acties?.toggleView(tag);
    }

    /** Maak een nieuwe condtag aan. */
    nieuweTag(naam: string): void {
        this.acties?.nieuweTag(naam);
    }

    /** Hernoem een condtag. */
    wijzigTag(oud: string, nieuw: string): void {
        this.acties?.wijzigTag(oud, nieuw);
    }

    /** Verwijder een condtag inclusief zijn elementen. */
    verwijderTag(tag: string): void {
        this.acties?.verwijderTag(tag);
    }
}
